import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { ConfirmTwoFactorSettingsParams, GetTwoFactorSettingsResponse, PartnerDataResource } from '../../resources';
import { twoFactorSettingsAtom } from '../../state/partner-data/twoFactorSettingsAtom';
import { BackendError } from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';
import { GetResourceHeaderWithToken } from '../useResource';

export type UseTwoFactorSettingsParams = {
  partnerDataResource: PartnerDataResource;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  clientId: string | undefined;
};

export const useTwoFactorSettingsCommon = ({
  getResourceHeaderWithToken,
  partnerDataResource,
  clientId,
}: UseTwoFactorSettingsParams) => {
  const [twoFactorSettings, setTwoFactorSettings] = useAtom(twoFactorSettingsAtom);

  const getTwoFactorSettings = useCallback(
    async (force: boolean = false): Promise<LoadableState<GetTwoFactorSettingsResponse>> => {
      try {
        if (twoFactorSettings.isLoaded && !force) {
          return twoFactorSettings;
        }
        if (!isDefined(clientId)) {
          return twoFactorSettings;
        }
        setTwoFactorSettings(Loadable.loading);
        const res = await partnerDataResource.getTwoFactorSettings({ clientId }, getResourceHeaderWithToken());
        const setting = Loadable.loaded({ twoFactorActivated: res.data.twoFactorActivated || false });
        setTwoFactorSettings(setting);
        return setting;
      } catch (e) {
        const setting = Loadable.failed;
        setTwoFactorSettings(setting);
        return setting;
      }
    },
    [twoFactorSettings, clientId, setTwoFactorSettings, partnerDataResource, getResourceHeaderWithToken],
  );

  const modifyTwoFactorSettings = useCallback(
    async (twoFactorActivated: boolean): Promise<string | undefined> => {
      if (!isDefined(clientId)) {
        return undefined;
      }
      const res = await partnerDataResource.modifyTwoFactorSettings(
        { clientId, modifyTwoFactorSettingsRequest: { twoFactorActivated } },
        getResourceHeaderWithToken(),
      );
      return res.data.confirmableActionId;
    },
    [clientId, partnerDataResource, getResourceHeaderWithToken],
  );

  const confirmTwoFactorSettings = useCallback(
    async (params: ConfirmTwoFactorSettingsParams): Promise<BackendError | undefined> => {
      if (!isDefined(clientId)) {
        return undefined;
      }
      try {
        const res = await partnerDataResource.confirmTwoFactorSettings(
          { clientId, confirmTwoFactorSettingsRequest: params },
          getResourceHeaderWithToken(),
        );
        setTwoFactorSettings(Loadable.loaded(res.data));
      } catch (error) {
        if (error instanceof AxiosError) {
          const data = error.response?.data ?? {};
          const errors = (data.errors ?? []) as BackendError[];
          if (Array.isArray(errors) && errors.length > 0 && isDefined(errors[0]) && typeof errors[0] === 'object') {
            return errors[0];
          }
        }
        throw error;
      }
      return undefined;
    },
    [clientId, setTwoFactorSettings, partnerDataResource, getResourceHeaderWithToken],
  );

  return {
    twoFactorSettings,
    getTwoFactorSettings,
    modifyTwoFactorSettings,
    confirmTwoFactorSettings,
  };
};
